import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchAllContracts } from '../../hooks/use-fetch-all-contracts';
import {
  BatchClientInsurerType,
  GetResolvedProcedureCodesDocument,
  GetResolvedProcedureCodesQuery,
  ProcedureCode,
  useGetBillingTypesQuery,
  useGetClientInsurersByIdsLazyQuery,
} from '../../types';

interface ApplicabilityTuple {
  clientId: number;
  insurerId: number;
  partnershipId: number | null;
  clientsInsurerId: number;
}

type ResolvedResult =
  | { tuple: ApplicabilityTuple; codes: ProcedureCode[]; error?: undefined }
  | { tuple: ApplicabilityTuple; codes: []; error: unknown };

interface UseApplicabilityDataResult {
  rows: BatchClientInsurerType[];
  loading: boolean;
  error: boolean;
}

const RESOLVE_CONCURRENCY = 10;

async function resolveInChunks<T, R>(
  items: T[],
  fn: (item: T) => Promise<R>,
  concurrency: number,
): Promise<R[]> {
  const results: R[] = [];
  for (let i = 0; i < items.length; i += concurrency) {
    const chunk = items.slice(i, i + concurrency);
    const chunkResults = await Promise.all(chunk.map(fn));
    results.push(...chunkResults);
  }
  return results;
}

export const useApplicabilityData = (
  partnershipId: number | null | undefined,
  insurerId: number | null | undefined,
  clientId: number | null | undefined,
  cptCode: string,
): UseApplicabilityDataResult => {
  const apolloClient = useApolloClient();
  const {
    data: billingTypesData,
    loading: billingTypesLoading,
    error: billingTypesError,
  } = useGetBillingTypesQuery();
  const { fetchAllContracts } = useFetchAllContracts();
  const [fetchClientInsurers] = useGetClientInsurersByIdsLazyQuery();

  const [rows, setRows] = useState<BatchClientInsurerType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const runIdRef = useRef(0);

  const run = useCallback(async (): Promise<void> => {
    if (!billingTypesData) return;

    const currentRunId = ++runIdRef.current;

    setLoading(true);
    setError(false);

    try {
      // Step 1: Filter billing types for waystar variants
      const waystarTypes = billingTypesData.getBillingTypes
        .map(bt => bt.value)
        .filter(v => v.toLowerCase().includes('waystar'));

      if (waystarTypes.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      // Step 2: Fetch contracts with waystar billing types matching the procedure code's scope
      const contractVars: Record<string, unknown> = {
        billingTypes: waystarTypes,
        size: 100,
      };
      if (partnershipId) contractVars.partnershipIds = [partnershipId];
      if (insurerId) contractVars.insurerIds = [insurerId];
      if (clientId) contractVars.clientIds = [clientId];

      const contracts = await fetchAllContracts(contractVars);

      if (currentRunId !== runIdRef.current) return;

      if (contracts.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      // Step 3: Deduplicate by (clientId, insurerId, partnershipId) and collect clientsInsurerId
      const tupleMap = new Map<string, ApplicabilityTuple>();
      for (const contract of contracts) {
        const pId = contract.partnershipId ?? null;
        const key = `${contract.clientId}-${contract.insurerId}-${pId}`;
        if (!tupleMap.has(key)) {
          tupleMap.set(key, {
            clientId: contract.clientId,
            insurerId: contract.insurerId,
            partnershipId: pId,
            clientsInsurerId: contract.clientsInsurerId,
          });
        }
      }

      const tuples = Array.from(tupleMap.values());

      // Step 4: Check resolved procedure codes for each tuple, chunked to avoid thundering herd
      const resolvedResults = await resolveInChunks<
        ApplicabilityTuple,
        ResolvedResult
      >(
        tuples,
        tuple =>
          apolloClient
            .query<GetResolvedProcedureCodesQuery>({
              query: GetResolvedProcedureCodesDocument,
              variables: {
                clientId: tuple.clientId,
                insurerId: tuple.insurerId,
                partnershipId: tuple.partnershipId,
              },
            })
            .then(
              (result): ResolvedResult => ({
                tuple,
                codes: result.data?.getResolvedProcedureCodes ?? [],
              }),
            )
            .catch(
              (err): ResolvedResult => ({
                tuple,
                codes: [],
                error: err,
              }),
            ),
        RESOLVE_CONCURRENCY,
      );

      if (currentRunId !== runIdRef.current) return;

      const hasFailures = resolvedResults.some(r => r.error);
      if (hasFailures) {
        setError(true);
      }

      // Step 5: Filter to tuples where the target cptCode is present (skip failed tuples)
      const matchingCiIds = resolvedResults
        .filter(r => !r.error && r.codes.some(c => c.cptCode === cptCode))
        .map(({ tuple }) => tuple.clientsInsurerId);

      const uniqueCiIds = [...new Set(matchingCiIds)];

      if (uniqueCiIds.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      // Step 6: Fetch client-insurer details for display
      const ciResult = await fetchClientInsurers({
        variables: { ids: uniqueCiIds },
      });

      if (currentRunId !== runIdRef.current) return;

      setRows(ciResult.data?.getClientInsurersByIds ?? []);
    } catch {
      if (currentRunId === runIdRef.current) {
        setError(true);
      }
    } finally {
      if (currentRunId === runIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    billingTypesData,
    partnershipId,
    insurerId,
    clientId,
    cptCode,
    fetchAllContracts,
    apolloClient,
    fetchClientInsurers,
  ]);

  useEffect(() => {
    if (billingTypesLoading) return;
    if (billingTypesError) {
      setLoading(false);
      return;
    }
    run();
  }, [billingTypesLoading, billingTypesError, run]);

  return {
    rows,
    loading: loading || billingTypesLoading,
    error: error || !!billingTypesError,
  };
};
